import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  CHAOS_THRESHOLD,
  FASCIST_POLICIES_IN_DECK,
  FASCIST_TRACK_SIZE,
  HITLER_CHANCELLOR_THRESHOLD,
  LIBERAL_POLICIES_IN_DECK,
  LIBERAL_TRACK_SIZE,
  MAX_PLAYERS,
  MIN_PLAYERS,
  ROLE_DISTRIBUTION,
  VETO_THRESHOLD,
  fascistTrack,
  hitlerKnowsFascists,
} from '@sh/engine'
import type { PowerKind } from '@sh/shared'
import { POWER_LABEL, PolicyTile } from '../components/Board.js'
import {
  ChancellorIcon,
  ExecutedIcon,
  OfflineIcon,
  PowerIcon,
  PresidentIcon,
  ReadyIcon,
  TermLimitedIcon,
  VetoIcon,
} from '../components/Icons.js'
import { Article } from './content/Article.js'

/**
 * The rules, for the person who is in the game right now and has forgotten one.
 *
 * Every number on this page is read from the engine's tables rather than typed
 * out here: the role distribution, both track lengths, the three thresholds and
 * the power on each fascist slot. A rules page that can disagree with the game
 * it documents is worse than no rules page, and this one cannot - if a table
 * changes, this page changes with it.
 *
 * It is a route of its own rather than a modal for two reasons. A player who
 * wants it usually wants it while something else is on their screen, and a
 * modal over a held veil is exactly the wrong thing to build; and /rules is a
 * thing the host can say out loud across a table.
 */

/** The five words this page is really about, each in the colour the rest of the app gives it. */
type TermKind = 'liberal' | 'fascist' | 'hitler' | 'president' | 'chancellor'

/**
 * A term, in the colour the board already uses for it. The word itself is the
 * child rather than a prop, so the prose keeps its own grammar - "liberals", "a
 * fascist policy", "the Presidency" all pass through unchanged and this only
 * tints them.
 *
 * Tint, and nothing else. There are around fifty of these across nine sections,
 * and at that frequency anything louder - a glyph, a weight, a pill - stops
 * being a mark and becomes texture, which on the one screen in this app that
 * anybody reads a sentence on is the expensive failure. The colours are pulled
 * a long way towards the body text for the same reason: enough hue to group the
 * word with its party at a glance, not enough to pull the eye off the rule.
 *
 * Nothing here means anything it does not also say. The word is the word; strip
 * the colour and the page is exactly what it was.
 */
function Term({ kind, children }: { kind: TermKind; children: ReactNode }) {
  return <span className={`term term--${kind}`}>{children}</span>
}

/** The longer half of each power. The short label comes from the board. */
const POWER_BLURB: Record<PowerKind, ReactNode> = {
  investigate: (
    <>
      The <Term kind="president">President</Term> looks at one player’s Party Membership —{' '}
      <Term kind="liberal">liberal</Term> or <Term kind="fascist">fascist</Term>, never the Secret
      Role, so <Term kind="hitler">Hitler</Term> reads as an ordinary{' '}
      <Term kind="fascist">fascist</Term>. Only the <Term kind="president">President</Term> sees
      it, and they may say anything they like about it afterwards. Nobody can be investigated
      twice.
    </>
  ),
  special_election: (
    <>
      The <Term kind="president">President</Term> hands the{' '}
      <Term kind="president">Presidency</Term> to any other living player, who runs the next
      election immediately. After that one round the order resumes from the seat after the{' '}
      <Term kind="president">President</Term> who called it, so a special election skips nobody’s
      turn.
    </>
  ),
  policy_peek: (
    <>
      The <Term kind="president">President</Term> looks at the top three tiles of the deck, in
      order, and puts them back untouched. Only they see it.
    </>
  ),
  execution: (
    <>
      The <Term kind="president">President</Term> kills a player. That seat is out for the rest of
      the game — no vote, no nomination, no power. If it was <Term kind="hitler">Hitler</Term>, the{' '}
      <Term kind="liberal">liberals</Term> win on the spot.
    </>
  ),
}

/** The three fascist boards, named the way the rulebook names them. */
const BRACKETS: { label: string; count: number }[] = [
  { label: '5–6 players', count: 5 },
  { label: '7–8 players', count: 7 },
  { label: '9–10 players', count: 9 },
]

/**
 * The key to the colour, placed where the page first needs it. Everything below
 * this list - the tracks, the win conditions, every tinted word in between -
 * leans on these three lines and none of it says so again.
 */
const PARTIES: { kind: TermKind; name: string; gloss: string }[] = [
  {
    kind: 'liberal',
    name: 'Liberals',
    gloss:
      'Most of the table. They are told nothing about anybody, and everything they ever learn comes from watching who votes for what.',
  },
  {
    kind: 'fascist',
    name: 'Fascists',
    gloss:
      'The few. They are told who each other are before the first round, and that is the whole of their advantage.',
  },
  {
    kind: 'hitler',
    name: 'Hitler',
    gloss:
      'Counted apart from the fascists above, and not one of the players they are told about. He carries their colour because he wins with them, and because an investigation of him says only “fascist”.',
  },
]

/**
 * The marks the seat list draws instead of words. This is the only place they
 * are written down: a player who has just seen a padlock beside a name has
 * nowhere else in the app to ask what it was.
 *
 * The labels are the seat list's own, verbatim, and the tints are its own
 * classes - so this cannot drift from the thing it is a legend for.
 */
const MARKS: { key: string; label: string; icon: ReactNode }[] = [
  { key: 'pres', label: 'President', icon: <PresidentIcon /> },
  { key: 'chan', label: 'Chancellor', icon: <ChancellorIcon /> },
  { key: 'term', label: 'Term-limited', icon: <TermLimitedIcon /> },
  { key: 'dead', label: 'Executed', icon: <ExecutedIcon /> },
  { key: 'ready', label: 'Ballot cast', icon: <ReadyIcon /> },
  { key: 'off', label: 'Offline', icon: <OfflineIcon /> },
]

const SECTIONS = [
  { id: 'idea', title: 'The idea' },
  { id: 'roles', title: 'Who is who' },
  { id: 'round', title: 'A round' },
  { id: 'terms', title: 'Term limits' },
  { id: 'chaos', title: 'Failed elections' },
  { id: 'powers', title: 'Presidential powers' },
  { id: 'veto', title: 'The veto' },
  { id: 'ends', title: 'How it ends' },
  { id: 'phone', title: 'On your phone' },
]

/** A fascist board, drawn with the same tiles the real board uses. */
function PowerTrack({ count }: { count: number }) {
  const track = fascistTrack(count)
  return (
    <div className="rules__track" role="list">
      {track.map((power, i) => (
        <div className="rules__slot" role="listitem" key={i}>
          <PolicyTile
            policy="fascist"
            filled={false}
            power={power}
            victory={i === FASCIST_TRACK_SIZE - 1}
          />
        </div>
      ))}
    </div>
  )
}

/**
 * The deck, as two piles: one of each policy, each as many tiles deep as the
 * deck holds, with the count written on top. The sentence above gives the two
 * numbers; this puts them where a player looks, and the fascist pile standing
 * nearly twice as tall makes "stacked against the liberals on purpose"
 * something they see rather than something they are told.
 *
 * One image to a screen reader, not seventeen: the piles sit inside a labelled
 * role="img", so the deck is announced once as a sentence.
 */
function Deck() {
  const piles = [
    { policy: 'liberal', count: LIBERAL_POLICIES_IN_DECK },
    { policy: 'fascist', count: FASCIST_POLICIES_IN_DECK },
  ] as const
  return (
    <div
      className="rules__deck"
      role="img"
      aria-label={`The deck: ${LIBERAL_POLICIES_IN_DECK} liberal tiles and ${FASCIST_POLICIES_IN_DECK} fascist tiles.`}
    >
      {piles.map(({ policy, count }) => (
        <div className={`rules__pile rules__pile--${policy}`} key={policy}>
          <span className="rules__pile-count">
            {count} <Term kind={policy}>{policy}</Term>
          </span>
          <span className="rules__pile-stack" style={{ ['--n' as string]: count }}>
            {Array.from({ length: count }, (_, i) => (
              <span className="rules__pile-card" style={{ ['--d' as string]: i }} key={i}>
                {i === count - 1 ? <PolicyTile policy={policy} size="lg" /> : <span className="rules__pile-edge" />}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  )
}

export function Rules() {
  const counts = Object.keys(ROLE_DISTRIBUTION)
    .map(Number)
    .sort((a, b) => a - b)

  return (
    <Article
      title="How to play"
      standfirst={
        <>
          Secret Hitler, in the order it happens at the table. The app enforces everything on this
          page except the talking. Rather see it?{' '}
          <Link to="/learn" data-testid="rules-tour">
            Take the two-minute tour
          </Link>
          .
        </>
      }
      sections={SECTIONS}
    >
        <section className="card" id="idea">
          <h2 className="card__title">The idea</h2>
          <p className="card__hint">
            {MIN_PLAYERS} to {MAX_PLAYERS} people, one table. Most of you are{' '}
            <Term kind="liberal">liberals</Term> and have no idea who anyone is. A few of you are{' '}
            <Term kind="fascist">fascists</Term>, and you know each other. One player is{' '}
            <Term kind="hitler">Hitler</Term>.
          </p>
          <p className="card__hint">
            Every round the table elects a government of two, and that government enacts one policy
            from a deck of {LIBERAL_POLICIES_IN_DECK} <Term kind="liberal">liberal</Term> and{' '}
            {FASCIST_POLICIES_IN_DECK} <Term kind="fascist">fascist</Term> tiles. The{' '}
            <Term kind="liberal">liberals</Term> are trying to enact {LIBERAL_TRACK_SIZE}{' '}
            <Term kind="liberal">liberal</Term> policies or shoot{' '}
            <Term kind="hitler">Hitler</Term>. The <Term kind="fascist">fascists</Term> are trying
            to enact {FASCIST_TRACK_SIZE} <Term kind="fascist">fascist</Term> ones or get{' '}
            <Term kind="hitler">Hitler</Term> elected <Term kind="chancellor">Chancellor</Term> late
            in the game. The deck is stacked against the <Term kind="liberal">liberals</Term> on
            purpose: a <Term kind="fascist">fascist</Term> policy going down is not proof of
            anything.
          </p>
          <Deck />
          <p className="card__warn">
            <strong>Everything else is talking.</strong> You may say anything about your role, your
            draw or your investigation, including things that are not true. You may never show a
            card — and on a phone you cannot, because nothing is on screen unless a finger is
            holding it there.
          </p>
        </section>

        <section className="card" id="roles">
          <h2 className="card__title">Who is who</h2>
          <p className="card__hint">
            Dealt at random when the host starts. Exactly one player is{' '}
            <Term kind="hitler">Hitler</Term> at every table size, and he is not counted among the{' '}
            <Term kind="fascist">fascists</Term> below — a five-player game has one{' '}
            <Term kind="fascist">fascist</Term> plus <Term kind="hitler">Hitler</Term>.
          </p>
          <ul className="rules__parties">
            {PARTIES.map((p) => (
              <li className="rules__party" key={p.kind}>
                <span className={`rules__tag rules__tag--${p.kind}`}>{p.name}</span>
                <span>{p.gloss}</span>
              </li>
            ))}
          </ul>
          <div className="rules__scroll">
            <table className="rules__table">
              <thead>
                <tr>
                  <th scope="col">Players</th>
                  <th scope="col" className="rules__col--liberal">
                    Liberals
                  </th>
                  <th scope="col" className="rules__col--fascist">
                    Fascists
                  </th>
                  <th scope="col">Hitler knows them</th>
                </tr>
              </thead>
              <tbody>
                {counts.map((n) => {
                  const dist = ROLE_DISTRIBUTION[n]
                  if (!dist) return null
                  return (
                    <tr key={n}>
                      <th scope="row">{n}</th>
                      <td>{dist.liberals}</td>
                      <td>{dist.fascists}</td>
                      <td>{hitlerKnowsFascists(n) ? 'yes' : 'no'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="card__hint">
            <Term kind="fascist">Fascists</Term> always learn who the other{' '}
            <Term kind="fascist">fascists</Term> are, and who <Term kind="hitler">Hitler</Term> is.
            At a small table <Term kind="hitler">Hitler</Term> learns them back; from seven players
            on, <Term kind="hitler">Hitler</Term> is as blind as a{' '}
            <Term kind="liberal">liberal</Term> and has to work out who is protecting him.
          </p>
        </section>

        <section className="card" id="round">
          <h2 className="card__title">A round</h2>
          <ol className="rules__steps">
            <li>
              <h3 className="rules__step-title">Election</h3>
              <p>
                The <Term kind="president">Presidency</Term> passes one seat clockwise every round,
                whether the last election passed or not — it is a rota, not a prize. The{' '}
                <Term kind="president">President</Term> nominates a{' '}
                <Term kind="chancellor">Chancellor</Term> from the eligible seats, then{' '}
                <em>everyone still alive</em> votes{' '}
                <span className="rules__vote rules__vote--ja">Ja</span> or{' '}
                <span className="rules__vote rules__vote--nein">Nein</span> at the same time,
                including the two of them.
              </p>
              <p>
                A strict majority elects. A tie is a failed election. Nothing is shown until the
                last ballot lands, and then every screen shows the full breakdown at once — who
                voted which way is public, always.
              </p>
            </li>
            <li>
              <h3 className="rules__step-title">Legislative session</h3>
              <p>
                The <Term kind="president">President</Term> draws three policy tiles, discards one
                face down and passes two to the <Term kind="chancellor">Chancellor</Term>. The{' '}
                <Term kind="chancellor">Chancellor</Term> enacts one and discards the other. Neither
                discard is ever revealed, to anyone.
              </p>
              <p>
                Play this part in silence: the government does not get to narrate what it drew until
                the tile is down. The app cannot enforce that one — it is the only rule here that is
                yours to keep.
              </p>
              <p>
                When fewer than three tiles are left, the discard pile is shuffled back in. The
                board shows both counts, so you can see a reshuffle coming.
              </p>
            </li>
            <li>
              <h3 className="rules__step-title">Executive action</h3>
              <p>
                If the <Term kind="fascist">fascist</Term> policy just enacted sits on a slot that
                grants a power, the <Term kind="president">President</Term> must use it before the
                next round begins. Powers come from the slot, not from the party of the government
                that filled it.
              </p>
            </li>
          </ol>
        </section>

        <section className="card" id="terms">
          <h2 className="card__title">Term limits</h2>
          <p className="card__hint">
            The <Term kind="president">President</Term> and{' '}
            <Term kind="chancellor">Chancellor</Term> of the last <em>elected</em> government cannot
            be nominated as <Term kind="chancellor">Chancellor</Term> in the next one. Once there
            are five or fewer players alive, only the last{' '}
            <Term kind="chancellor">Chancellor</Term> is locked out — with a table that small,
            locking out two seats would leave nobody to nominate.
          </p>
          <p className="card__hint">
            A failed election does not term-limit anybody, because nobody was elected. The app
            simply does not offer an ineligible seat, and the board marks the seats that are out, so
            this is not arithmetic anyone at the table has to redo.
          </p>
        </section>

        <section className="card" id="chaos">
          <h2 className="card__title">Failed elections</h2>
          <p className="card__hint">
            Every failed election advances the election tracker. On the{' '}
            {CHAOS_THRESHOLD === 3 ? 'third' : `${CHAOS_THRESHOLD}th`} in a row the country falls
            into chaos: the top tile of the deck is enacted immediately, with no government and no
            power, the tracker resets, and all term limits are forgotten — the next{' '}
            <Term kind="president">President</Term> may nominate anyone.
          </p>
          <p className="card__hint">
            Enacting any policy resets the tracker, however it got there. A vetoed agenda counts as
            a failed election too.
          </p>
        </section>

        <section className="card" id="powers">
          <h2 className="card__title">Presidential powers</h2>
          <p className="card__hint">
            Which slot grants which power depends on how many people sat down. This is your board:
          </p>

          <div className="rules__boards">
            {BRACKETS.map((bracket) => (
              <div className="rules__board" key={bracket.label}>
                <h3 className="rules__board-label">{bracket.label}</h3>
                <PowerTrack count={bracket.count} />
              </div>
            ))}
          </div>

          <dl className="rules__powers">
            {(['investigate', 'special_election', 'policy_peek', 'execution'] as PowerKind[]).map(
              (kind) => (
                <div className="rules__power" key={kind}>
                  <dt>
                    <span className="rules__power-icon" aria-hidden="true">
                      <PowerIcon kind={kind} />
                    </span>
                    {POWER_LABEL[kind]}
                  </dt>
                  <dd>{POWER_BLURB[kind]}</dd>
                </div>
              ),
            )}
          </dl>

          <p className="card__hint">
            A policy the people enact in chaos grants nothing, and a power with no legal target is
            skipped and announced.
          </p>
        </section>

        <section className="card" id="veto">
          <h2 className="card__title rules__card-title">
            <span className="rules__title-icon" aria-hidden="true">
              <VetoIcon />
            </span>
            The veto
          </h2>
          <p className="card__hint">
            From the {VETO_THRESHOLD}th <Term kind="fascist">fascist</Term> policy on, the
            government may kill an agenda outright. The{' '}
            <Term kind="chancellor">Chancellor</Term> proposes the veto; if the{' '}
            <Term kind="president">President</Term> consents, both tiles are discarded and nothing
            is enacted. If the <Term kind="president">President</Term> refuses, the{' '}
            <Term kind="chancellor">Chancellor</Term> must enact one of the two after all.
          </p>
          <p className="card__hint">
            A successful veto counts as a failed election, tracker and all — three of them still
            throw the country into chaos.
          </p>
        </section>

        <section className="card" id="ends">
          <h2 className="card__title">How it ends</h2>
          <ul className="rules__ends">
            <li>
              <span className="rules__tag rules__tag--liberal">Liberals win</span>
              {LIBERAL_TRACK_SIZE} <Term kind="liberal">liberal</Term> policies are enacted.
            </li>
            <li>
              <span className="rules__tag rules__tag--liberal">Liberals win</span>
              <Term kind="hitler">Hitler</Term> is executed.
            </li>
            <li>
              <span className="rules__tag rules__tag--fascist">Fascists win</span>
              {FASCIST_TRACK_SIZE} <Term kind="fascist">fascist</Term> policies are enacted.
            </li>
            <li>
              <span className="rules__tag rules__tag--fascist">Fascists win</span>{' '}
              <Term kind="hitler">Hitler</Term> is elected{' '}
              <Term kind="chancellor">Chancellor</Term> after {HITLER_CHANCELLOR_THRESHOLD}{' '}
              <Term kind="fascist">fascist</Term> policies are already down. The board draws a band
              under exactly those slots, because this is the one a table forgets.
            </li>
          </ul>
        </section>

        <section className="card" id="phone">
          <h2 className="card__title">On your phone</h2>
          <p className="card__hint">
            <strong>The seat list says these without words.</strong> Each mark below is the one the
            board draws beside a name; hold or tab onto one there and it hands its word back.
          </p>
          <ul className="rules__marks">
            {MARKS.map((m) => (
              <li className="rules__mark" key={m.key}>
                <span className={`rules__mark-icon mark--${m.key}`} aria-hidden="true">
                  {m.icon}
                </span>
                {m.label}
              </li>
            ))}
          </ul>
          <p className="card__hint">
            <strong>Press and hold to see a secret.</strong> Your role, an investigation result, a
            peek at the deck: they exist on screen only while you are holding them, and they are
            gone the instant you let go, switch tabs or lock the phone. Nothing is hidden behind a
            blur that somebody could photograph.
          </p>
          <p className="card__hint">
            <strong>The policies you have to choose from work the other way.</strong> You show them
            with a button and hide them with the same one, because you have to think with them in
            front of you. The cards themselves are the buttons — press one to discard or enact it —
            so nothing can be played without being looked at, and the hand puts itself away the
            moment the phone is locked or switched away from.
          </p>
          <p className="card__hint">
            <strong>
              You are told who your fellow <Term kind="fascist">fascists</Term> are exactly once
            </strong>
            , during the reveal, and never again. Read it properly the first time — the app will not
            remind you.
          </p>
          <p className="card__hint">
            <strong>A cast ballot is not handed back</strong>, not even to you. You are sitting next
            to the people it is a secret from.
          </p>
          <p className="card__hint">
            <strong>There is no game log.</strong> The board shows what a physical table would show
            right now. Arguing about what happened three rounds ago is the game, not a feature.
          </p>
          <p className="card__hint">
            <strong>If a phone dies</strong>, open the site on any device and join the lobby under
            the name you were playing under. The game knows that seat is yours and asks for a claim
            PIN; the host issues one from Host tools and reads it out, and you are back in with your
            role intact.
          </p>
        </section>
    </Article>
  )
}
